/**
 * Live push-to-talk transport (ENG-1721), the second talk-down transport
 * next to the clip upload (useTalkDown). Only reachable behind the
 * `showLiveTalkDown` flag. Talks to monitoring-api talk-down sessions:
 * START (POST), STATUS (GET) and STOP (DELETE) on
 * `streaming/talk-down/{camera_id}/[{session_id}/]`.
 *
 * Session model: the first press opens a session (mic, START, then WHIP
 * publish immediately, since the publish token is short-lived) and the
 * PeerConnection then stays open across holds. Between holds the mic track
 * is disabled (`enabled = false`): the sender keeps flowing silence frames,
 * which is what keeps the server-side Job alive (it ends a session after
 * 30 s with no frames). The sender is never stopped or removed between
 * holds. Each hold is capped at 60 s client-side.
 *
 * The client tears the session down at the earliest of: 60 s idle since the
 * last hold, unmount, camera change, page hide, or STATUS reporting
 * ended/failed (or 404, the session is gone).
 *
 * Closing: DELETE only sets stop_requested, and the Job can hold the camera
 * for up to ~20 s after it (a Job waiting for a publisher). So after any
 * teardown we initiate (errors, drops, idle, page hide) the hook polls
 * STATUS every second until ended/failed/404 (max ~25 s) and the button
 * stays disabled meanwhile. START is never retried automatically: a new
 * START only follows a new press once closing is over (ENG-1721 prod test,
 * where re-presses after a WHIP failure hit 409 three times in ~2 s).
 *
 * STATUS drives everything else: polled every second until `ready`, then
 * every ~5 s while open, then until a final state after our STOP. Audio
 * only flows once `ready`, so the first words are not lost.
 *
 * Audio input here is getUserMedia straight into the PeerConnection; this
 * hook does not use useAudioRecorder (that is the clip transport's input).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  describeLiveTalkDownOutcome,
  describeLiveTalkDownOutcomeDetail,
  describeLiveTalkDownStartError,
  describeTalkDownErrorDetail,
  TALK_DOWN_LIVE_ENDPOINT_LABEL,
  fetchLiveTalkDownStatus,
  isLiveTalkDownFinished,
  isTalkDownInProgress,
  LIVE_TALK_DOWN_ENDED_MESSAGE,
  LIVE_TALK_DOWN_IDLE_MS,
  LIVE_TALK_DOWN_MAX_SECONDS,
  LIVE_TALK_DOWN_OPEN_INTERVAL_MS,
  LIVE_TALK_DOWN_OUTCOME_INTERVAL_MS,
  LIVE_TALK_DOWN_OUTCOME_TIMEOUT_MS,
  LIVE_TALK_DOWN_READY_INTERVAL_MS,
  LIVE_TALK_DOWN_READY_TIMEOUT_MS,
  LIVE_TALK_DOWN_RECENT_SESSION_MS,
  LiveOutcomeResult,
  LiveTalkDownSession,
  LiveTalkDownStatus,
  pollLiveTalkDownOutcome,
  startLiveTalkDown,
  stopLiveTalkDown,
} from "./talkDownApi";
import {
  MIC_DENIED_MESSAGE,
  MIC_FAILED_MESSAGE,
  MIC_NOT_FOUND_MESSAGE,
} from "./useAudioRecorder";
import {
  WhipAbortedError,
  whipDelete,
  WhipDeps,
  WhipError,
  whipPublish,
} from "./whipPublish";
import { useTalkingCamerasStore } from "./talkingCamerasStore";
import { noteTalkDownErrorCode } from "./talkDownCapabilityStore";

export type LiveTalkDownPhase =
  | "idle"
  | "connecting"
  | "live"
  | "open"
  | "waiting"
  /** Our previous session is still shutting down; no new START yet. */
  | "closing"
  | "ended"
  | "failed"
  | "error";

export interface LiveTalkDownState {
  phase: LiveTalkDownPhase;
  message: string;
  /** Technical cause for the staff-only detail line. */
  detail?: string;
}

export const IDLE_LIVE_TALK_DOWN: LiveTalkDownState = {
  phase: "idle",
  message: "",
};

export const LIVE_CONNECTING_MESSAGE = "Connecting...";
export const LIVE_READY_MESSAGE = "Ready. Hold to talk.";
export const LIVE_UNSUPPORTED_MESSAGE =
  "Live talk-down isn't supported in this browser.";
export const LIVE_CONNECT_FAILED_MESSAGE =
  "Couldn't open the live audio connection. Try again in a moment.";
export const LIVE_NOT_READY_MESSAGE =
  "The camera didn't get ready to listen. Try again in a moment.";
export const LIVE_DROPPED_MESSAGE =
  "The live audio connection dropped. Hold to try again.";
export const LIVE_WAITING_MESSAGE = "Ending...";
export const LIVE_CLOSING_MESSAGE = "Still closing the last session…";
export const liveHoldHint = (maxSeconds: number) =>
  `Release to stop (up to ${maxSeconds} s)`;
export const liveCapMessage = (maxSeconds: number) =>
  `Reached the ${maxSeconds} s limit. Release, then hold to keep talking.`;

/** "0:05" */
export const formatLiveElapsed = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};
const TICK_MS = 250;

/** STATUS poll timings; all overridable for tests. */
export interface LiveStatusPollOptions {
  readyIntervalMs?: number;
  openIntervalMs?: number;
  readyTimeoutMs?: number;
  outcomeIntervalMs?: number;
  outcomeTimeoutMs?: number;
}

export interface UseLiveTalkDownOptions {
  cameraId: string | number | null | undefined;
  cameraName: string;
  statusPoll?: LiveStatusPollOptions;
  /** Idle teardown; test hook. */
  idleMs?: number;
  /** Injectable fetch / RTCPeerConnection for the WHIP exchange. */
  whipDeps?: WhipDeps;
}

export interface LiveTalkDown {
  state: LiveTalkDownState;
  /** Whole seconds into the current hold, while live. */
  elapsedSeconds: number;
  /** Per-hold cap (60 s, or the session's cap if lower). */
  maxSeconds: number;
  /**
   * The outcome is being fetched or the last session is still closing; the
   * button should not start another.
   */
  busy: boolean;
  press: () => void;
  release: () => void;
  reset: () => void;
}

interface LiveSessionContext {
  cameraId: string | number;
  session: LiveTalkDownSession | null;
  stream: MediaStream | null;
  pc: RTCPeerConnection | null;
  resourceUrl: string | null;
  ready: boolean;
  everLive: boolean;
  closed: boolean;
  abort: AbortController;
}

type TeardownReason =
  | "idle"
  | "hidden"
  | "camera"
  | "unmount"
  | "reset"
  | "dropped"
  | "error"
  /** STATUS reported ended/failed, or 404 (gone). */
  | "server";

interface TeardownDetail {
  message?: string;
  /** Staff-only technical cause. */
  detail?: string;
  status?: LiveTalkDownStatus | null;
}

const describeMicError = (reason: unknown): string | null => {
  const name = (reason as { name?: string } | null)?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return MIC_DENIED_MESSAGE;
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return MIC_NOT_FOUND_MESSAGE;
  }
  if (name === "NotReadableError" || name === "AbortError") {
    return MIC_FAILED_MESSAGE;
  }
  return null;
};

const stopTracks = (stream: MediaStream | null) => {
  stream?.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      // Already stopped.
    }
  });
};

/** Mute/unmute without touching the sender: muted still sends silence. */
const setMicOn = (ctx: LiveSessionContext, on: boolean) => {
  ctx.stream?.getAudioTracks().forEach((track) => {
    track.enabled = on;
  });
};

// Resolves after `ms`, or immediately once `signal` aborts.
const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done);
  });

export const useLiveTalkDown = ({
  cameraId,
  cameraName,
  statusPoll,
  idleMs = LIVE_TALK_DOWN_IDLE_MS,
  whipDeps,
}: UseLiveTalkDownOptions): LiveTalkDown => {
  const [state, setState] = useState<LiveTalkDownState>(IDLE_LIVE_TALK_DOWN);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(LIVE_TALK_DOWN_MAX_SECONDS);

  // Inputs read from timers and async continuations.
  const inputsRef = useRef({
    cameraId,
    cameraName,
    statusPoll,
    idleMs,
    whipDeps,
  });
  inputsRef.current = { cameraId, cameraName, statusPoll, idleMs, whipDeps };

  const ctxRef = useRef<LiveSessionContext | null>(null);
  const heldRef = useRef(false);
  const capHitRef = useRef(false);
  const maxSecondsRef = useRef(LIVE_TALK_DOWN_MAX_SECONDS);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const outcomeAbortRef = useRef<AbortController | null>(null);
  const unmountedRef = useRef(false);
  /** Our last session on this camera, and when it was last known alive. */
  const lastSessionRef = useRef<{ cameraId: string | number; at: number }>();

  const clearTick = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
  };
  const clearIdle = () => {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = null;
  };
  const cancelOutcome = () => {
    outcomeAbortRef.current?.abort();
    outcomeAbortRef.current = null;
  };

  const showOutcome = (
    camId: string | number,
    status: LiveTalkDownStatus | null
  ) => {
    const outcome = describeLiveTalkDownOutcome(
      status,
      inputsRef.current.cameraName
    );
    setState({
      phase: outcome.ok ? "ended" : "failed",
      message: outcome.message,
      detail: outcome.ok
        ? undefined
        : describeLiveTalkDownOutcomeDetail(status),
    });
    // ENG-1721: a camera without a speaker hides its button for the rest
    // of the page session. Last: it can unmount this control.
    noteTalkDownErrorCode(camId, status?.error_code);
  };

  const noteSession = (camId: string | number) => {
    lastSessionRef.current = { cameraId: camId, at: Date.now() };
  };

  /**
   * After our STOP: poll STATUS (every ~1 s, up to ~25 s) until the session
   * is ended/failed/gone, showing `pending` meanwhile. While this runs
   * `outcomeAbortRef` is set, which blocks press(). Never starts anything.
   */
  const closeOut = (
    camId: string | number,
    sessionId: string,
    pending: LiveTalkDownState,
    finish: (result: Exclude<LiveOutcomeResult, { kind: "aborted" }>) => void
  ) => {
    cancelOutcome();
    const controller = new AbortController();
    outcomeAbortRef.current = controller;
    const current = () =>
      outcomeAbortRef.current === controller && !controller.signal.aborted;
    const opts = inputsRef.current.statusPoll;
    setState(pending);
    pollLiveTalkDownOutcome(camId, sessionId, {
      intervalMs: opts?.outcomeIntervalMs ?? LIVE_TALK_DOWN_OUTCOME_INTERVAL_MS,
      timeoutMs: opts?.outcomeTimeoutMs ?? LIVE_TALK_DOWN_OUTCOME_TIMEOUT_MS,
      signal: controller.signal,
    }).then((result) => {
      if (!current() || result.kind === "aborted") return;
      outcomeAbortRef.current = null;
      noteSession(camId);
      finish(result);
    });
  };

  /** After our STOP of a session that was used: show its final state. */
  const fetchOutcome = (camId: string | number, sessionId: string) => {
    closeOut(
      camId,
      sessionId,
      { phase: "waiting", message: LIVE_WAITING_MESSAGE },
      (result) => {
        if (result.kind === "timeout") {
          setState({ phase: "ended", message: LIVE_TALK_DOWN_ENDED_MESSAGE });
          return;
        }
        showOutcome(camId, result.status);
      }
    );
  };

  /** After our STOP: show "Still closing…" until it is gone, then `after`. */
  const waitForClose = (
    camId: string | number,
    sessionId: string,
    after: LiveTalkDownState
  ) => {
    closeOut(
      camId,
      sessionId,
      { phase: "closing", message: LIVE_CLOSING_MESSAGE },
      () => setState(after)
    );
  };

  const teardown = (reason: TeardownReason, detail: TeardownDetail = {}) => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.closed) return;
    ctx.closed = true;
    ctxRef.current = null;
    clearTick();
    clearIdle();
    ctx.abort.abort();
    stopTracks(ctx.stream);
    try {
      ctx.pc?.close();
    } catch {
      // Already closed.
    }
    if (ctx.resourceUrl) {
      void whipDelete(ctx.resourceUrl, inputsRef.current.whipDeps);
    }
    if (ctx.session) noteSession(ctx.cameraId);
    // The server already ended a "server" session; everything else asks it.
    // On unmount the DELETEs are all we do: nobody is left to poll for.
    if (ctx.session && reason !== "server") {
      stopLiveTalkDown(ctx.cameraId, ctx.session.session_id).catch(() => {
        // Best effort: the Job also ends when the WHIP session ends.
      });
    }
    if (reason === "unmount" || unmountedRef.current) return;
    setElapsedSeconds(0);
    // Sessions we asked to stop still hold the camera for a while: wait
    // for them to finish before another START is allowed.
    const sessionId =
      ctx.session && reason !== "server" ? ctx.session.session_id : null;
    const settle = (after: LiveTalkDownState) => {
      if (sessionId) waitForClose(ctx.cameraId, sessionId, after);
      else setState(after);
    };
    switch (reason) {
      case "server":
        showOutcome(ctx.cameraId, detail.status ?? null);
        return;
      case "error":
        settle({
          phase: "error",
          message: detail.message ?? LIVE_CONNECT_FAILED_MESSAGE,
          detail: detail.detail,
        });
        return;
      case "dropped":
        settle({
          phase: "error",
          message: ctx.ready
            ? LIVE_DROPPED_MESSAGE
            : LIVE_CONNECT_FAILED_MESSAGE,
        });
        return;
      case "idle":
      case "hidden":
        if (ctx.everLive && sessionId) {
          fetchOutcome(ctx.cameraId, sessionId);
          return;
        }
        settle(IDLE_LIVE_TALK_DOWN);
        return;
      default:
        // "camera" / "reset": the caller resets the state itself.
        setState(IDLE_LIVE_TALK_DOWN);
    }
  };

  const armIdle = () => {
    clearIdle();
    idleRef.current = setTimeout(() => {
      idleRef.current = null;
      fnRef.current.teardown("idle");
    }, inputsRef.current.idleMs);
  };

  const beginHold = (ctx: LiveSessionContext) => {
    clearIdle();
    clearTick();
    ctx.everLive = true;
    setMicOn(ctx, true);
    const max = maxSecondsRef.current;
    const startedAt = Date.now();
    setElapsedSeconds(0);
    setState({ phase: "live", message: liveHoldHint(max) });
    tickRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setElapsedSeconds(elapsed);
      if (elapsed >= max) {
        capHitRef.current = true;
        fnRef.current.endHold(liveCapMessage(max));
      }
    }, TICK_MS);
  };

  const endHold = (message = LIVE_READY_MESSAGE) => {
    clearTick();
    const ctx = ctxRef.current;
    if (!ctx) return;
    setMicOn(ctx, false);
    setElapsedSeconds(0);
    setState({ phase: "open", message });
    armIdle();
  };

  const markReady = (ctx: LiveSessionContext) => {
    if (ctx.ready || ctx.closed) return;
    ctx.ready = true;
    if (heldRef.current && !capHitRef.current) {
      beginHold(ctx);
    } else {
      setState({ phase: "open", message: LIVE_READY_MESSAGE });
      armIdle();
    }
  };

  const onPcState = (ctx: LiveSessionContext, pcState: string) => {
    if (ctxRef.current !== ctx || ctx.closed) return;
    if (pcState === "failed" || pcState === "closed") teardown("dropped");
  };

  /**
   * STATUS loop for an open session: every second until ready, then every
   * ~5 s. Ends the session when the server reports ended/failed or 404.
   * Transient errors keep polling.
   */
  const statusLoop = async (
    ctx: LiveSessionContext,
    session: LiveTalkDownSession
  ) => {
    const alive = () => ctxRef.current === ctx && !ctx.closed;
    const opts = inputsRef.current.statusPoll;
    const readyIntervalMs =
      opts?.readyIntervalMs ?? LIVE_TALK_DOWN_READY_INTERVAL_MS;
    const openIntervalMs =
      opts?.openIntervalMs ?? LIVE_TALK_DOWN_OPEN_INTERVAL_MS;
    const readyTimeoutMs =
      opts?.readyTimeoutMs ?? LIVE_TALK_DOWN_READY_TIMEOUT_MS;
    const startedAt = Date.now();
    while (alive()) {
      let status: LiveTalkDownStatus | null | undefined;
      try {
        status = await fetchLiveTalkDownStatus(
          ctx.cameraId,
          session.session_id,
          ctx.abort.signal
        );
      } catch {
        status = undefined; // transient: try again
      }
      if (!alive()) return;
      if (status !== undefined && isLiveTalkDownFinished(status)) {
        fnRef.current.teardown("server", { status });
        return;
      }
      if (status && (status.ready || status.status === "ready")) {
        fnRef.current.markReady(ctx);
      }
      if (!ctx.ready && Date.now() - startedAt >= readyTimeoutMs) {
        fnRef.current.teardown("error", { message: LIVE_NOT_READY_MESSAGE });
        return;
      }
      await wait(
        ctx.ready ? openIntervalMs : readyIntervalMs,
        ctx.abort.signal
      );
    }
  };

  const openSession = async (camId: string | number) => {
    const ctx: LiveSessionContext = {
      cameraId: camId,
      session: null,
      stream: null,
      pc: null,
      resourceUrl: null,
      ready: false,
      everLive: false,
      closed: false,
      abort: new AbortController(),
    };
    ctxRef.current = ctx;
    const alive = () => ctxRef.current === ctx && !ctx.closed;
    setState({ phase: "connecting", message: LIVE_CONNECTING_MESSAGE });

    try {
      const hasWebRtc =
        typeof window !== "undefined" &&
        (typeof (window as unknown as { RTCPeerConnection?: unknown })
          .RTCPeerConnection === "function" ||
          Boolean(inputsRef.current.whipDeps?.createPeerConnection));
      if (!navigator.mediaDevices?.getUserMedia || !hasWebRtc) {
        teardown("error", { message: LIVE_UNSUPPORTED_MESSAGE });
        return;
      }

      // Mic first: a denied mic should not open a session on the camera.
      const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      if (!alive()) {
        stopTracks(stream);
        return;
      }
      ctx.stream = stream;
      setMicOn(ctx, false);

      const session = await startLiveTalkDown(camId);
      if (!alive()) {
        stopLiveTalkDown(camId, session.session_id).catch(() => {});
        return;
      }
      ctx.session = session;
      // Client per-hold cap; the server's max_seconds is the whole session.
      const max =
        typeof session.max_seconds === "number" && session.max_seconds > 0
          ? Math.min(LIVE_TALK_DOWN_MAX_SECONDS, session.max_seconds)
          : LIVE_TALK_DOWN_MAX_SECONDS;
      maxSecondsRef.current = max;
      setMaxSeconds(max);

      // Publish straight away: the WHIP token lives 120 s.
      const publication = await whipPublish(
        {
          whipUrl: session.whip_url,
          stream,
          onConnectionStateChange: (s) => fnRef.current.onPcState(ctx, s),
          signal: ctx.abort.signal,
        },
        inputsRef.current.whipDeps
      );
      if (!alive()) {
        publication.pc.close();
        if (publication.resourceUrl) {
          void whipDelete(publication.resourceUrl, inputsRef.current.whipDeps);
        }
        return;
      }
      ctx.pc = publication.pc;
      ctx.resourceUrl = publication.resourceUrl;
      void statusLoop(ctx, session);
    } catch (reason) {
      if (!alive() || reason instanceof WhipAbortedError) return;
      // 409 right after our own session on this camera: that session is
      // still shutting down. Say so; never retry START on our own.
      const recent = lastSessionRef.current;
      if (
        !ctx.session &&
        isTalkDownInProgress(reason) &&
        recent &&
        String(recent.cameraId) === String(camId) &&
        Date.now() - recent.at < LIVE_TALK_DOWN_RECENT_SESSION_MS
      ) {
        teardown("error", { message: LIVE_CLOSING_MESSAGE });
        return;
      }
      const message =
        describeMicError(reason) ??
        (reason instanceof WhipError
          ? reason.stage === "unsupported"
            ? LIVE_UNSUPPORTED_MESSAGE
            : LIVE_CONNECT_FAILED_MESSAGE
          : describeLiveTalkDownStartError(reason));
      const detail =
        reason instanceof WhipError
          ? `WHIP ${reason.stage}${reason.status ? ` ${reason.status}` : ""}`
          : describeMicError(reason)
          ? `getUserMedia ${(reason as { name?: string })?.name ?? "error"}`
          : describeTalkDownErrorDetail(reason, TALK_DOWN_LIVE_ENDPOINT_LABEL);
      teardown("error", { message, detail });
    }
  };

  const press = () => {
    const camId = inputsRef.current.cameraId;
    if (camId == null || camId === "") return;
    // Outcome still being fetched / last session still closing.
    if (outcomeAbortRef.current) return;
    heldRef.current = true;
    capHitRef.current = false;
    clearIdle();
    const ctx = ctxRef.current;
    if (ctx && !ctx.closed) {
      if (ctx.ready) beginHold(ctx);
      else setState({ phase: "connecting", message: LIVE_CONNECTING_MESSAGE });
      return;
    }
    void openSession(camId);
  };

  const release = () => {
    if (!heldRef.current) return;
    heldRef.current = false;
    const wasCapped = capHitRef.current;
    capHitRef.current = false;
    const ctx = ctxRef.current;
    if (!ctx || ctx.closed) return;
    if (ctx.ready) {
      if (wasCapped) {
        // Already silenced at the cap; the idle clock is already running.
        setState({ phase: "open", message: LIVE_READY_MESSAGE });
        return;
      }
      endHold();
    } else {
      // Still connecting: keep going so the next hold is instant, but the
      // idle clock starts now.
      armIdle();
    }
  };

  const reset = () => {
    cancelOutcome();
    heldRef.current = false;
    teardown("reset");
    setState(IDLE_LIVE_TALK_DOWN);
  };

  // Timers, effects and async continuations call through this ref so they
  // always reach the latest closures.
  const fnRef = useRef({
    teardown,
    endHold,
    markReady,
    onPcState,
    press,
    release,
    reset,
  });
  fnRef.current = {
    teardown,
    endHold,
    markReady,
    onPcState,
    press,
    release,
    reset,
  };

  // A new camera ends any session (and outcome) that belongs to the old one.
  useEffect(() => {
    fnRef.current.teardown("camera");
    outcomeAbortRef.current?.abort();
    outcomeAbortRef.current = null;
    heldRef.current = false;
    setState(IDLE_LIVE_TALK_DOWN);
    setElapsedSeconds(0);
  }, [cameraId]);

  // Half-duplex with listen-in (ENG-2136): while the operator holds to talk
  // (or has pressed and the session is still connecting), tell the live
  // players to mute this camera's audio so the mic doesn't hear itself back.
  const holdKeyRef = useRef<string>();
  if (!holdKeyRef.current) {
    holdKeyRef.current = `talk-${Math.random().toString(36).slice(2)}`;
  }
  const talking = state.phase === "live" || state.phase === "connecting";
  useEffect(() => {
    const key = holdKeyRef.current as string;
    const { setHold } = useTalkingCamerasStore.getState();
    setHold(
      key,
      talking && cameraId != null && cameraId !== "" ? String(cameraId) : null
    );
  }, [talking, cameraId]);
  useEffect(
    () => () => {
      useTalkingCamerasStore
        .getState()
        .setHold(holdKeyRef.current as string, null);
    },
    []
  );

  // Page hidden (tab switch, minimise, navigation away): end the session.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        fnRef.current.teardown("hidden");
      }
    };
    const onPageHide = () => fnRef.current.teardown("hidden");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      outcomeAbortRef.current?.abort();
      outcomeAbortRef.current = null;
      fnRef.current.teardown("unmount");
    };
  }, []);

  return {
    state,
    elapsedSeconds,
    maxSeconds,
    busy: state.phase === "waiting" || state.phase === "closing",
    press: useCallback(() => fnRef.current.press(), []),
    release: useCallback(() => fnRef.current.release(), []),
    reset: useCallback(() => fnRef.current.reset(), []),
  };
};
